import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { APIProvider, Map as GoogleMap, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "../components/ui/dialog";
import {
  MapPin, Search, ArrowLeft, Star, Loader2, Compass, Filter,
  Save, ShoppingCart, Check, Landmark, Castle, TreePine, Church,
  BookOpen, Drama, Trophy, SlidersHorizontal, Navigation2, Clock,
  Trash2, Headphones, Package, ChevronUp, ChevronDown, Map as MapIcon,
  AlertCircle
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { contentApi, userApi } from "../lib/api";
import api from "../lib/api";
import { toast } from "sonner";
import { addBreadcrumb, captureError, captureMessage } from "../lib/sentry";
import { BottomNav } from "../components/BottomNav";
import { DesktopSidebar } from "../components/DesktopSidebar";
import {
  getPurchaseSummary,
  isPurchasablePlace,
  isSelectableTourPlace,
  TRANSIENT_CITY_ID_PREFIX,
} from "../lib/tourPlannerUtils";
import { resolveMediaUrl } from "../lib/apiConfig";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const GOOGLE_MAPS_MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID;
let reportedMissingGoogleMapsMapId = false;

const CATEGORY_CONFIG = {
  landmark:    { icon: Star,     color: "bg-amber-500",   hex: "#f59e0b", label: "Landmarks" },
  museum:      { icon: Landmark, color: "bg-indigo-500",  hex: "#6366f1", label: "Museums & Galleries" },
  historical:  { icon: Castle,   color: "bg-stone-600",   hex: "#57534e", label: "Historical Sites" },
  natural:     { icon: TreePine, color: "bg-emerald-500", hex: "#10b981", label: "Parks & Gardens" },
  religious:   { icon: Church,   color: "bg-purple-600",  hex: "#9333ea", label: "Churches & Temples" },
  library:     { icon: BookOpen, color: "bg-cyan-600",    hex: "#0891b2", label: "Libraries" },
  theater:     { icon: Drama,    color: "bg-rose-500",    hex: "#f43f5e", label: "Theatres" },
  stadium:     { icon: Trophy,   color: "bg-orange-600",  hex: "#ea580c", label: "Stadiums & Arenas" },
};

const COUNT_OPTIONS = [
  { count: 3,  label: "Top 3" },
  { count: 5,  label: "Top 5" },
  { count: 10, label: "Top 10" },
];

const MIN_REVIEW_OPTIONS = [0, 10, 25, 50, 100];

const RADIUS_OPTIONS = [
  { km: 1,  label: "1 km" },
  { km: 3,  label: "3 km" },
  { km: 5,  label: "5 km" },
  { km: 10, label: "10 km" },
];

const HISTORY_KEY = "cv_tour_search_history";
const getSearchHistory = () => {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]"); } catch { return []; }
};
const addSearchHistory = (entry) => {
  const hist = getSearchHistory().filter(h => h.name !== entry.name);
  hist.unshift(entry);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(hist.slice(0, 10)));
};
const clearSearchHistory = () => localStorage.removeItem(HISTORY_KEY);

const TourMapContent = ({ places, destination }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || !places.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    places.forEach(p => bounds.extend({ lat: p.latitude, lng: p.longitude }));
    if (destination) bounds.extend({ lat: destination.lat, lng: destination.lng });
    map.fitBounds(bounds, 40);
  }, [map, places, destination]);
  return null;
};

const PlaceImage = ({ src, alt, icon: Icon, className = "w-16 h-16 rounded-lg", muted = false }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div className={`${className} bg-primary/10 border border-primary/10 flex items-center justify-center overflow-hidden`}>
        <Icon className={`${muted ? "w-6 h-6" : "w-7 h-7"} text-primary/70`} />
      </div>
    );
  }

  return (
    <img
      src={resolveMediaUrl(src)}
      alt={alt}
      className={`${className} object-cover`}
      onError={() => setFailed(true)}
    />
  );
};

/* ─── Place card — image left, number overlay, selection via border ─── */
const PlaceCard = ({ place, index, selected, selectionMode, onToggle, onView, progress }) => {
  const cat = CATEGORY_CONFIG[place.category] || CATEGORY_CONFIG.landmark;
  const Icon = cat.icon;
  const isDownloading = progress != null && progress < 100;
  const isDone = progress === 100;
  const isTip = place.is_starting_point;
  const selectable = selectionMode && isSelectableTourPlace(place);
  const [tipExpanded, setTipExpanded] = useState(false);

  // Smart address/description: avoid showing same text twice, strip country
  const rawAddr = (place.address || "").trim();
  const addr = rawAddr.replace(/,\s*[A-Za-zÀ-ÿ\s]+$/, ""); // strip trailing country
  const desc = (place.description || "").trim();
  const descMatchesAddr = desc && addr && desc.toLowerCase() === addr.toLowerCase();
  const showAddress = !!addr;
  const showDescription = desc && !descMatchesAddr;

  const handleClick = () => {
    if (selectable) onToggle(place.id);
    else onView(place);
  };

  // Starting Point tip card
  if (isTip) {
    return (
      <div className="relative" data-testid="tour-city-tip">
        <Card
          className={`cursor-pointer border-primary/30 bg-primary/[0.04] hover:border-primary/50 transition-all ${
            selected && selectionMode ? "ring-2 ring-primary border-primary" : ""
          }`}
          onClick={handleClick}
        >
          <CardContent className="flex items-start gap-3 py-3 px-3">
            <div className="relative flex-shrink-0">
              <PlaceImage src={place.image_url} alt={place.name} icon={Compass} />
              <div className="absolute -top-1.5 -left-1.5 w-5 h-5 rounded-full flex items-center justify-center text-white shadow bg-primary">
                <Compass className="w-3 h-3" />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <Badge variant="secondary" className="text-[9px] h-4 px-1.5 mb-1 bg-primary/10 text-primary border-0">
                Starting point
              </Badge>
              <p className="font-medium text-sm leading-tight">{place.name}</p>
              {desc ? (
                <p
                  className={`text-xs text-muted-foreground mt-0.5 ${tipExpanded ? "" : "line-clamp-2"}`}
                  onClick={(e) => {
                    if (selectionMode) return;
                    e.stopPropagation();
                    setTipExpanded(prev => !prev);
                  }}
                  data-testid="city-tip-description"
                >
                  {desc}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mt-0.5">City overview audio guide</p>
              )}
            </div>
            <div className="flex-shrink-0 pt-0.5">
              {selectionMode ? (
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  selected ? "bg-primary border-primary" : "border-muted-foreground/30"
                }`}>
                  {selected && <Check className="w-3.5 h-3.5 text-white" />}
                </div>
              ) : (
                <Headphones className="w-4 h-4 text-primary/60" />
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="relative" data-testid={`tour-place-${place.id}`}>
      <Card
        className={`cursor-pointer transition-all ${
          selected && selectionMode
            ? "ring-2 ring-primary border-primary"
            : isDone
            ? "ring-2 ring-emerald-500 border-emerald-500"
            : "hover:border-primary/30"
        }`}
        onClick={handleClick}
      >
        <CardContent className="flex items-start gap-3 py-3 px-3">
          {/* Image with rank number overlay */}
          <div className="relative flex-shrink-0">
            <PlaceImage src={place.image_url} alt={place.name} icon={Icon} muted />
            <div
              className="absolute -top-1.5 -left-1.5 w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold shadow"
              style={{ backgroundColor: cat.hex }}
            >
              {index}
            </div>
            {isDone && (
              <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shadow">
                <Check className="w-3 h-3 text-white" />
              </div>
            )}
          </div>

          {/* Info */}
          <div className="min-w-0 flex-1">
            <p className="font-medium text-sm leading-tight">{place.name}</p>
            {showAddress && (
              <p className="text-[11px] text-muted-foreground truncate mt-0.5 flex items-center gap-0.5">
                <MapPin className="w-2.5 h-2.5 flex-shrink-0" />
                {addr}
              </p>
            )}
            {showDescription && !showAddress && (
              <p className="text-xs text-muted-foreground/70 italic line-clamp-1 mt-0.5">{desc}</p>
            )}
            {showDescription && showAddress && (
              <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{desc}</p>
            )}
            <Badge variant="secondary" className="text-[10px] h-4 px-1.5 mt-1 bg-white dark:bg-white/90 border border-border/50" style={{ color: cat.hex }}>
              {cat.label}
            </Badge>
          </div>

          {/* Rating + distance column */}
          <div className="flex flex-col items-end gap-0.5 flex-shrink-0 pt-0.5">
            {place.rating > 0 && (
              <div className="flex items-center gap-1">
                <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                <span className="text-xs font-medium">{place.rating.toFixed(1)}</span>
              </div>
            )}
            {place.review_count > 0 && (
              <span className="text-[10px] text-muted-foreground">({place.review_count.toLocaleString()})</span>
            )}
            {place.distance_km != null && place.distance_km > 0 && (
              <span className="text-[10px] text-muted-foreground flex items-center gap-0.5 mt-0.5">
                <Navigation2 className="w-2.5 h-2.5" />
                {place.distance_km < 1 ? `${(place.distance_km * 1000).toFixed(0)}m` : `${place.distance_km.toFixed(1)} km`}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Progress bar */}
      {isDownloading && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-muted rounded-b-lg overflow-hidden">
          <div className="h-full bg-primary transition-all duration-500 ease-out" style={{ width: `${progress}%` }} />
        </div>
      )}
    </div>
  );
};

const TourPlannerPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  const [step, setStep] = useState(1);

  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [destination, setDestination] = useState(null);
  const [searchHistory, setSearchHistory] = useState(getSearchHistory);
  const searchTimer = useRef(null);

  const [selectedCategories, setSelectedCategories] = useState(Object.keys(CATEGORY_CONFIG));
  const [selectedCount, setSelectedCount] = useState(5);
  const [selectedRadius, setSelectedRadius] = useState(3);
  const [minimumReviewCount, setMinimumReviewCount] = useState(10);

  const [places, setPlaces] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);

  // Selection mode
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedPlaces, setSelectedPlaces] = useState(new Set());
  const [purchasing, setPurchasing] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [buyingAll, setBuyingAll] = useState(false);
  const [insufficientOpen, setInsufficientOpen] = useState(false);
  const [creditsNeeded, setCreditsNeeded] = useState(0);

  // Saved trips
  const [savedTrips, setSavedTrips] = useState([]);
  const [savedTripsLoading, setSavedTripsLoading] = useState(true);
  const [hasPendingDownloads, setHasPendingDownloads] = useState(false);

  useEffect(() => {
    if (GOOGLE_MAPS_API_KEY && !GOOGLE_MAPS_MAP_ID && !reportedMissingGoogleMapsMapId) {
      reportedMissingGoogleMapsMapId = true;
      captureMessage("Google Maps Map ID is missing", "warning", {
        page: "TourPlannerPage",
        env_var: "REACT_APP_GOOGLE_MAPS_MAP_ID",
      });
    }
  }, []);

  // Load session state — restore previous results when navigating back to Plan
  // Only restore places + destination, NOT filter settings (those always use defaults)
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem("cv_tour_last");
      if (saved) {
        const d = JSON.parse(saved);
        if (d.places?.length) {
          setPlaces(d.places);
          setDestination(d.destination);
          setStep(3);
        }
      }
    } catch {}
  }, []);

  // Save session state so clicking "Plan" from another page restores results
  useEffect(() => {
    if (places.length > 0 && destination) {
      sessionStorage.setItem("cv_tour_last", JSON.stringify({
        places, destination, categories: selectedCategories, count: selectedCount, radius: selectedRadius,
      }));
    }
  }, [places, destination, selectedCategories, selectedCount, selectedRadius]);


  // Fetch saved trips on mount
  useEffect(() => {
    if (!user) { setSavedTripsLoading(false); return; }
    userApi.getTourPlans()
      .then(r => setSavedTrips(r.data.plans || []))
      .catch((error) => {
        captureError(error, { action: "load_saved_tour_plans" });
      })
      .finally(() => setSavedTripsLoading(false));
  }, [user]);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!query || query.length < 2) { setSuggestions([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await contentApi.searchAutocomplete(query);
        setSuggestions(res.data.suggestions || []);
      } catch (error) {
        captureError(error, { action: "tour_destination_autocomplete", queryLength: query.length });
        setSuggestions([]);
      }
      setSearchLoading(false);
    }, 300);
  }, []);

  const selectDestination = async (suggestion) => {
    setSuggestions([]);
    setSearchQuery(suggestion.name);
    try {
      const res = await contentApi.getPlaceDetails(suggestion.place_id);
      const d = res.data;
      const dest = { name: d.name || suggestion.name, lat: d.latitude, lng: d.longitude, description: d.address || suggestion.description, place_id: suggestion.place_id };
      setDestination(dest);
      addSearchHistory({ name: dest.name, lat: dest.lat, lng: dest.lng, description: dest.description, place_id: suggestion.place_id });
      setSearchHistory(getSearchHistory());
      setStep(2);
    } catch (error) {
      captureError(error, { action: "tour_destination_details", placeId: suggestion.place_id });
      toast.error("Could not get location details");
    }
  };

  const loadFromHistory = (entry) => {
    setDestination({ name: entry.name, lat: entry.lat, lng: entry.lng, description: entry.description || "", place_id: entry.place_id || null });
    setSearchQuery(entry.name);
    setStep(2);
  };

  const handleClearHistory = () => { clearSearchHistory(); setSearchHistory([]); };

  const toggleCategory = (cat) => {
    setSelectedCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };

  const resolveStartingPointMarker = useCallback(async (dest) => {
    let placeId = dest?.place_id;
    if (!placeId && dest?.name) {
      try {
        const res = await contentApi.searchAutocomplete(dest.name, dest.latitude || dest.lat, dest.longitude || dest.lng);
        placeId = res.data?.suggestions?.[0]?.place_id || null;
      } catch (error) {
        captureError(error, { action: "find_tour_starting_place_id", destination: dest.name });
      }
    }
    if (!placeId) return null;
    try {
      const res = await contentApi.ensureMarkerFromPlace(placeId);
      return res.data?.id ? res.data : null;
    } catch (error) {
      captureError(error, { action: "ensure_tour_starting_marker", placeId });
      return null;
    }
  }, []);

  const buildStartingPoint = useCallback((dest, planData, marker) => ({
    id: marker?.id || `${TRANSIENT_CITY_ID_PREFIX}${Date.now()}`,
    name: marker?.name || dest.name,
    latitude: marker?.latitude || dest.lat,
    longitude: marker?.longitude || dest.lng,
    description: planData.destination_description || marker?.description || "",
    address: marker?.address || "",
    category: marker?.category || "landmark",
    rating: marker?.rating || 0,
    review_count: marker?.review_count || 0,
    distance_km: 0,
    is_starting_point: true,
    place_id: dest.place_id || marker?.google_place_id || marker?.place_id || null,
    image_url: marker?.image_url || planData.destination_image || "",
  }), []);

  const generatePlan = async () => {
    if (!destination) return;
    setResultsLoading(true);
    setSelectedPlaces(new Set());
    setDownloadProgress({});
    setSelectionMode(false);
    setBuyingAll(false);
    addBreadcrumb("Tour plan generation requested", "tour.plan", {
      destination: destination.name,
      categories: selectedCategories,
      count: selectedCount,
      radiusKm: selectedRadius,
      minimumReviewCount,
    });
    try {
      const res = await api.post("/content/plan-tour", {
        latitude: destination.lat, longitude: destination.lng,
        categories: selectedCategories, count: selectedCount, max_radius_km: selectedRadius,
        destination_name: destination.name,
        destination_place_id: destination.place_id || "",
        min_review_count: minimumReviewCount,
      });
      const results = res.data.places || [];
      const startingMarker = await resolveStartingPointMarker(destination);
      const cityTip = buildStartingPoint(destination, res.data, startingMarker);
      setPlaces([cityTip, ...results]);
      setStep(3);
    } catch (err) {
      if (!err.response || err.response.status >= 500) {
        captureError(err, { action: "generate_tour_plan", destination: destination.name });
      }
      toast.error(err.response?.data?.detail || "Failed to generate tour plan");
    }
    setResultsLoading(false);
  };

  const togglePlace = (placeId) => {
    setSelectedPlaces(prev => {
      const next = new Set(prev);
      if (next.has(placeId)) next.delete(placeId); else next.add(placeId);
      return next;
    });
  };

  const viewPlace = async (place) => {
    if (place.is_starting_point && place.place_id) {
      // For city starting points, ensure a real marker exists before navigating
      try {
        const res = await contentApi.ensureMarkerFromPlace(place.place_id);
        const marker = res.data;
        if (marker?.id) {
          navigate(`/explore?lat=${marker.latitude}&lng=${marker.longitude}&zoom=14&marker=${marker.id}`);
          return;
        }
      } catch (err) {
        captureError(err, { action: "view_tour_place", placeId: place.place_id });
        toast.error("Could not load this place. Try again later.");
        return;
      }
    }
    navigate(`/explore?lat=${place.latitude}&lng=${place.longitude}&zoom=17&marker=${place.id}`);
  };

  const simulateDownloadProgress = (placeIds) => {
    const progress = {};
    placeIds.forEach(id => { progress[id] = 0; });
    setDownloadProgress({ ...progress });
    placeIds.forEach((id, index) => {
      const startDelay = index * 400;
      [15, 35, 55, 75, 90, 100].forEach((val, stepIdx) => {
        setTimeout(() => {
          setDownloadProgress(prev => ({ ...prev, [id]: val }));
        }, startDelay + (stepIdx + 1) * 350);
      });
    });
  };

  const resolvePurchasePlaceIds = async (rawPlaceIds) => {
    const resolvedIds = [];
    const replacements = new Map();

    for (const rawPlaceId of rawPlaceIds) {
      const place = places.find(p => p.id === rawPlaceId);
      if (!place) continue;
      if (isPurchasablePlace(place)) {
        resolvedIds.push(place.id);
        continue;
      }

      if (place.is_starting_point) {
        const marker = await resolveStartingPointMarker(place);
        if (marker?.id) {
          replacements.set(place.id, marker);
          resolvedIds.push(marker.id);
          continue;
        }
      }

      throw new Error("Starting point could not be prepared for purchase");
    }

    if (replacements.size > 0) {
      setPlaces(prev => prev.map((place) => {
        const marker = replacements.get(place.id);
        if (!marker) return place;
        return {
          ...place,
          id: marker.id,
          name: marker.name || place.name,
          latitude: marker.latitude || place.latitude,
          longitude: marker.longitude || place.longitude,
          address: marker.address || place.address,
          category: marker.category || place.category,
          rating: marker.rating || place.rating,
          review_count: marker.review_count || place.review_count,
          image_url: marker.image_url || place.image_url,
          place_id: marker.google_place_id || marker.place_id || place.place_id,
        };
      }));
      setSelectedPlaces(prev => {
        const next = new Set();
        prev.forEach((id) => next.add(replacements.get(id)?.id || id));
        return next;
      });
    }

    return resolvedIds;
  };

  const handlePurchase = async () => {
    setConfirmOpen(false);
    const purchaseSummary = getPurchaseSummary({ places, selectedIds: selectedPlaces, buyingAll });
    if (purchasing || purchaseSummary.placeIds.length === 0) return;
    setPurchasing(true);
    addBreadcrumb("Tour bundle purchase requested", "tour.purchase", {
      count: purchaseSummary.placeIds.length,
      buyingAll,
      destination: destination?.name || "",
      language: user?.default_audio_language || "en",
    });
    try {
      const placeIds = await resolvePurchasePlaceIds(purchaseSummary.placeIds);
      const res = await userApi.bundlePurchase({
        place_ids: placeIds,
        count: placeIds.length,
        destination_name: destination?.name || "",
        bundle_all: buyingAll,
        language: user?.default_audio_language || "en",
      });
      const { purchased, credits_used, folder_name } = res.data;
      if (purchased > 0) simulateDownloadProgress(placeIds);
      setHasPendingDownloads(purchased > 0);
      if (folder_name) {
        toast.success(`${purchased} guides purchased and saved to "${folder_name}"`);
      } else {
        toast.success(`${purchased} guides purchased! (${credits_used} credits used)`);
      }
      setSelectionMode(false);
      setBuyingAll(false);
      if (refreshUser) refreshUser();
    } catch (err) {
      if (err.response?.status === 402) {
        setCreditsNeeded(purchaseSummary.credits);
        setInsufficientOpen(true);
      } else {
        if (!err.response || err.response.status >= 500) {
          captureError(err, { action: "purchase_tour_bundle", count: purchaseSummary.placeIds.length, buyingAll });
        }
        if (err.message === "Starting point could not be prepared for purchase") {
          toast.error("Could not prepare the starting point for purchase. Try searching for the destination again, then regenerate the trip.");
        } else {
          toast.error(err.response?.data?.detail || "Purchase failed");
        }
      }
    }
    setPurchasing(false);
  };

  // Auto-save: name = "Place Name - DD/MM HH:MM"
  const handleSave = async () => {
    if (!destination || !places.length) return;
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, "0");
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const hh = String(now.getHours()).padStart(2, "0");
    const min = String(now.getMinutes()).padStart(2, "0");
    const autoName = `${destination.name} - ${dd}/${mm} ${hh}:${min}`;
    try {
      const res = await userApi.saveTourPlan({
        name: autoName,
        destination_name: destination.name,
        destination_lat: destination.lat,
        destination_lng: destination.lng,
        categories: selectedCategories,
        count: selectedCount,
        max_radius_km: selectedRadius,
        min_review_count: minimumReviewCount,
        place_ids: places.filter(isPurchasablePlace).map(p => p.id),
      });
      setSavedTrips(prev => [{ ...res.data, name: autoName }, ...prev]);
      toast.success("Saved to My cultural trips", { duration: 3000 });
    } catch (err) {
      if (!err.response || err.response.status >= 500) {
        captureError(err, { action: "save_tour_plan", destination: destination.name });
      }
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const loadSavedTrip = async (trip) => {
    const dest = {
      name: trip.destination_name,
      lat: trip.destination_lat,
      lng: trip.destination_lng,
      place_id: trip.destination_place_id || null,
    };
    const cats = trip.categories?.length ? trip.categories : Object.keys(CATEGORY_CONFIG);
    const count = trip.count || 5;
    const radius = trip.max_radius_km || 3;
    const savedThreshold = Number(trip.min_review_count);
    const minimumReviews = savedThreshold === 0 || MIN_REVIEW_OPTIONS.includes(savedThreshold)
      ? savedThreshold
      : 10;
    setDestination(dest);
    setSelectedCategories(cats);
    setSelectedCount(count);
    setSelectedRadius(radius);
    setMinimumReviewCount(minimumReviews);
    setSearchQuery(trip.destination_name || "");
    // Go directly to results
    setResultsLoading(true);
    setSelectedPlaces(new Set());
    setDownloadProgress({});
    setSelectionMode(false);
    setBuyingAll(false);
    setStep(3);
    try {
      const res = await api.post("/content/plan-tour", {
        latitude: dest.lat, longitude: dest.lng,
        categories: cats, count, max_radius_km: radius,
        destination_name: dest.name,
        destination_place_id: dest.place_id || "",
        min_review_count: minimumReviews,
      });
      const results = res.data.places || [];
      const startingMarker = await resolveStartingPointMarker(dest);
      const cityTip = buildStartingPoint(dest, res.data, startingMarker);
      setPlaces([cityTip, ...results]);
    } catch (err) {
      if (!err.response || err.response.status >= 500) {
        captureError(err, { action: "load_saved_trip_results", tripId: trip.id });
      }
      toast.error(err.response?.data?.detail || "Failed to load trip results");
      setStep(1);
    }
    setResultsLoading(false);
  };

  const deleteSavedTrip = async (tripId, e) => {
    e.stopPropagation();
    try {
      await userApi.deleteTourPlan(tripId);
      setSavedTrips(prev => prev.filter(t => t.id !== tripId));
      toast.success("Trip deleted");
    } catch (error) {
      captureError(error, { action: "delete_tour_plan", tripId });
      toast.error("Failed to delete");
    }
  };

  const selectedSummary = getPurchaseSummary({ places, selectedIds: selectedPlaces, buyingAll: false });
  const buyAllSummary = getPurchaseSummary({ places, selectedIds: selectedPlaces, buyingAll: true });
  const numSelected = selectedSummary.count;
  const allDone = Object.values(downloadProgress).length > 0 && Object.values(downloadProgress).every(v => v === 100);

  // Bundle pricing applies to any selected set, including a real starting point marker.
  const purchasablePlaces = buyAllSummary.purchasablePlaces;
  const bundleCount = buyAllSummary.count;
  const bundleCost = buyAllSummary.credits;
  const regularCost = buyAllSummary.regularCredits;
  const hasBundleDiscount = buyAllSummary.hasDiscount;
  const selectedCost = selectedSummary.credits;

  // Number of items for confirm dialog
  const confirmCount = buyingAll ? bundleCount : numSelected;
  const confirmCredits = buyingAll ? bundleCost : selectedCost;

  // Check if user has enough credits before opening confirmation
  const openPurchaseConfirm = (isBuyAll) => {
    const cost = isBuyAll ? bundleCost : selectedCost;
    const userCredits = user?.credits || 0;
    if (userCredits < cost) {
      setCreditsNeeded(cost);
      setInsufficientOpen(true);
      return;
    }
    setBuyingAll(isBuyAll);
    setConfirmOpen(true);
  };

  const handleBack = () => {
    if (hasPendingDownloads) {
      toast.info("Your audio guides are being generated in the background. Check your Library when ready.");
    }
    if (step === 3) setStep(2);
    else if (step === 2) setStep(1);
    else navigate("/explore");
  };

  // --- Expandable map/results split for Step 3 ---
  const MAP_SNAP_COLLAPSED = 20;   // % - mostly results
  const MAP_SNAP_DEFAULT   = 35;   // % - balanced
  const MAP_SNAP_EXPANDED  = 70;   // % - mostly map
  const [mapHeightPct, setMapHeightPct] = useState(MAP_SNAP_DEFAULT);
  const dragRef = useRef(null);
  const containerRef = useRef(null);

  const handleDragStart = useCallback((startY) => {
    const container = containerRef.current;
    if (!container) return null;
    const rect = container.getBoundingClientRect();
    return { startY, containerTop: rect.top, containerHeight: rect.height, startPct: mapHeightPct };
  }, [mapHeightPct]);

  const handleDragMove = useCallback((clientY, dragState) => {
    if (!dragState) return;
    const { containerTop, containerHeight, startPct, startY } = dragState;
    const deltaY = clientY - startY;
    const deltaPct = (deltaY / containerHeight) * 100;
    const newPct = Math.max(10, Math.min(85, startPct + deltaPct));
    setMapHeightPct(newPct);
  }, []);

  const handleDragEnd = useCallback(() => {
    // Snap to nearest state
    const snaps = [MAP_SNAP_COLLAPSED, MAP_SNAP_DEFAULT, MAP_SNAP_EXPANDED];
    let closest = snaps[0];
    let minDist = Math.abs(mapHeightPct - snaps[0]);
    for (const s of snaps) {
      const d = Math.abs(mapHeightPct - s);
      if (d < minDist) { minDist = d; closest = s; }
    }
    setMapHeightPct(closest);
  }, [mapHeightPct]);

  // Touch handlers for the drag handle
  const onTouchStartHandle = useCallback((e) => {
    const touch = e.touches[0];
    dragRef.current = handleDragStart(touch.clientY);
  }, [handleDragStart]);

  const onTouchMoveHandle = useCallback((e) => {
    if (!dragRef.current) return;
    e.preventDefault();
    handleDragMove(e.touches[0].clientY, dragRef.current);
  }, [handleDragMove]);

  const onTouchEndHandle = useCallback(() => {
    dragRef.current = null;
    handleDragEnd();
  }, [handleDragEnd]);

  // Mouse handlers for desktop
  const onMouseDownHandle = useCallback((e) => {
    e.preventDefault();
    const state = handleDragStart(e.clientY);
    if (!state) return;
    const onMove = (ev) => handleDragMove(ev.clientY, state);
    const onUp = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); handleDragEnd(); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [handleDragStart, handleDragMove, handleDragEnd]);

  const toggleMapExpand = useCallback(() => {
    setMapHeightPct(prev => prev >= MAP_SNAP_DEFAULT ? MAP_SNAP_COLLAPSED : MAP_SNAP_EXPANDED);
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col lg:pl-64">
      <DesktopSidebar active="/plan-tour" />
      <header className="sticky top-0 bg-background/95 backdrop-blur border-b z-20 flex-shrink-0">
        <div className="max-w-lg mx-auto flex items-center gap-2 px-4 py-3">
          <Button variant="ghost" size="icon"
            onClick={handleBack}
            className="rounded-xl" data-testid="tour-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold">Plan a Trip</h1>
            {destination && step > 1 && <p className="text-xs text-muted-foreground truncate">{destination.name}</p>}
          </div>
          {step === 3 && places.length > 0 && (
            <Button variant="outline" size="sm" className="rounded-lg h-8 text-xs gap-1.5 flex-shrink-0"
              onClick={handleSave} data-testid="save-plan-btn"
            >
              <Save className="w-3.5 h-3.5" /> Save trip
            </Button>
          )}
        </div>
      </header>

      {/* Steps 1 & 2 use normal scrollable layout */}
      {step !== 3 && (
        <main className="max-w-lg mx-auto px-4 py-5 space-y-5 pb-20 flex-1">
          {/* Step 1 */}
          {step === 1 && (
            <section data-testid="tour-step-search">
              <div className="flex items-center gap-2 mb-4">
                <Compass className="w-5 h-5 text-primary" />
                <h2 className="text-base font-semibold">Where are you going?</h2>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search for a city or place..." className="pl-10 h-12 rounded-xl text-base"
                  value={searchQuery} onChange={(e) => handleSearch(e.target.value)} autoFocus data-testid="tour-search-input" />
                {searchLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
              </div>
              {suggestions.length > 0 && (
                <div className="mt-2 border rounded-xl overflow-hidden divide-y" data-testid="tour-suggestions">
                  {suggestions.map((s) => (
                    <button key={s.place_id} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/50 transition-colors"
                      onClick={() => selectDestination(s)} data-testid={`tour-suggestion-${s.place_id}`}>
                      <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{s.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{s.description}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Saved cultural trips */}
              {!searchQuery && savedTrips.length > 0 && (
                <div className="mt-5" data-testid="saved-cultural-trips">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Save className="w-3.5 h-3.5 text-primary" />
                    <span className="text-xs font-semibold text-primary uppercase tracking-wider">Saved cultural trips</span>
                  </div>
                  <div className="space-y-1">
                    {savedTrips.map((trip) => (
                      <div
                        key={trip.id}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-lg hover:bg-muted/50 transition-colors group cursor-pointer"
                        role="button"
                        tabIndex={0}
                        onClick={() => loadSavedTrip(trip)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            loadSavedTrip(trip);
                          }
                        }}
                        data-testid={`saved-trip-${trip.id}`}
                      >
                        <Compass className="w-4 h-4 text-primary/60 flex-shrink-0" />
                        <div className="min-w-0 flex-1" data-testid={`load-saved-trip-${trip.id}`}>
                          <p className="text-sm font-medium truncate">{trip.name}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {trip.count || 5} places &middot; {trip.categories?.length || "all"} categories
                          </p>
                        </div>
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-destructive transition-colors p-2 -mr-1 rounded-md hover:bg-destructive/10"
                          onClick={(e) => deleteSavedTrip(trip.id, e)}
                          data-testid={`delete-trip-${trip.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Search history */}
              {!searchQuery && searchHistory.length > 0 && (
                <div className="mt-5" data-testid="tour-search-history">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-xs font-medium text-muted-foreground">Previous searches</span>
                    </div>
                    <button className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1 transition-colors"
                      onClick={handleClearHistory} data-testid="clear-search-history-btn"><Trash2 className="w-3 h-3" /> Clear</button>
                  </div>
                  <div className="space-y-1">
                    {searchHistory.map((h, i) => (
                      <button key={`${h.name}-${i}`} className="w-full flex items-center gap-3 px-3 py-2 text-left rounded-lg hover:bg-muted/50 transition-colors"
                        onClick={() => loadFromHistory(h)} data-testid={`history-item-${i}`}>
                        <Clock className="w-3.5 h-3.5 text-muted-foreground/40 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{h.name}</p>
                          {h.description && <p className="text-xs text-muted-foreground truncate">{h.description}</p>}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {!searchQuery && searchHistory.length === 0 && savedTrips.length === 0 && (
                <div className="mt-8 text-center">
                  <Compass className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">Enter a destination to discover the best places to visit</p>
                </div>
              )}
            </section>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <section data-testid="tour-step-filters">
              <div className="flex items-center gap-2 mb-3">
                <Filter className="w-5 h-5 text-primary" />
                <h2 className="text-base font-semibold">What interests you?</h2>
                <button className="ml-auto text-xs text-primary hover:underline"
                  onClick={() => setSelectedCategories(selectedCategories.length === Object.keys(CATEGORY_CONFIG).length ? [] : Object.keys(CATEGORY_CONFIG))}>
                  {selectedCategories.length === Object.keys(CATEGORY_CONFIG).length ? "Clear all" : "Select all"}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-5">
                {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => {
                  const Icon = cfg.icon; const active = selectedCategories.includes(key);
                  return (
                    <button key={key} className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm transition-all ${active ? "border-primary bg-primary/5 text-foreground" : "border-border text-muted-foreground hover:border-primary/30"}`}
                      onClick={() => toggleCategory(key)} data-testid={`tour-cat-${key}`}>
                      <div className={`w-6 h-6 rounded-md flex items-center justify-center text-white flex-shrink-0 ${cfg.color}`}><Icon className="w-3.5 h-3.5" /></div>
                      <span className="truncate text-xs flex-1">{cfg.label}</span>
                      <Check className={`w-3.5 h-3.5 ml-auto flex-shrink-0 transition-opacity ${active ? "text-primary opacity-100" : "opacity-0"}`} />
                    </button>
                  );
                })}
              </div>
              <div className="mb-5">
                <p className="text-sm font-medium mb-2">Maximum distance from center/location</p>
                <div className="grid grid-cols-4 gap-2">
                  {RADIUS_OPTIONS.map((opt) => (
                    <button key={opt.km} className={`flex flex-col items-center gap-0.5 p-2.5 rounded-xl border transition-all ${selectedRadius === opt.km ? "border-primary bg-primary/5 ring-1 ring-primary/20" : "border-border hover:border-primary/30"}`}
                      onClick={() => setSelectedRadius(opt.km)} data-testid={`tour-radius-${opt.km}`}>
                      <span className="text-sm font-bold">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="mb-5">
                <p className="text-sm font-medium mb-2">How many recommendations?</p>
                <div className="grid grid-cols-3 gap-2">
                  {COUNT_OPTIONS.map((opt) => (
                    <button key={opt.count} className={`flex flex-col items-center gap-1 p-3 rounded-xl border transition-all ${selectedCount === opt.count ? "border-primary bg-primary/5 ring-1 ring-primary/20" : "border-border hover:border-primary/30"}`}
                      onClick={() => setSelectedCount(opt.count)} data-testid={`tour-count-${opt.count}`}>
                      <span className="text-lg font-bold">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="mb-5" data-testid="tour-filter-review-count">
                <p className="text-sm font-medium mb-2">Filter out results with fewer reviews than</p>
                <div className="grid grid-cols-4 gap-2">
                  {MIN_REVIEW_OPTIONS.map((reviewCount) => (
                    <button
                      key={reviewCount}
                      type="button"
                      className={`p-3 rounded-xl border text-sm font-bold transition-all ${minimumReviewCount === reviewCount ? "border-primary bg-primary/5 ring-1 ring-primary/20" : "border-border hover:border-primary/30"}`}
                      onClick={() => setMinimumReviewCount(reviewCount)}
                      aria-pressed={minimumReviewCount === reviewCount}
                      data-testid={`tour-min-review-count-${reviewCount}`}
                    >
                      {reviewCount}
                    </button>
                  ))}
                </div>
              </div>
              <Button className="w-full h-12 rounded-xl text-base" onClick={generatePlan} disabled={resultsLoading || selectedCategories.length === 0} data-testid="tour-generate-btn">
                {resultsLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Finding highlights...</> : <><Compass className="w-4 h-4 mr-2" /> Show cultural highlights</>}
              </Button>
            </section>
          )}
        </main>
      )}

      {/* Step 3 — Expandable split view */}
      {step === 3 && (
        <>
          {resultsLoading ? (
            <div className="flex-1 flex items-center justify-center pb-20">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Finding cultural highlights...</p>
              </div>
            </div>
          ) : (
            <div ref={containerRef} className="flex flex-col overflow-hidden" style={{ height: "calc(100dvh - 57px - 64px)" }} data-testid="tour-step-results">
              {/* Map section */}
              {places.length > 0 && GOOGLE_MAPS_API_KEY && GOOGLE_MAPS_MAP_ID && (
                <div
                  className="flex-shrink-0 relative transition-[height] duration-300 ease-out"
                  style={{ height: `${mapHeightPct}%` }}
                  data-testid="tour-map"
                >
                  <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                    <GoogleMap defaultZoom={12} defaultCenter={destination ? { lat: destination.lat, lng: destination.lng } : { lat: 51.5, lng: -0.1 }}
                      mapId={GOOGLE_MAPS_MAP_ID} gestureHandling="cooperative" disableDefaultUI={true} style={{ width: "100%", height: "100%" }}>
                      <TourMapContent places={places} destination={destination} />
                      {places.map((place, i) => {
                        const cat = CATEGORY_CONFIG[place.category] || CATEGORY_CONFIG.landmark;
                        if (place.is_starting_point) {
                          return (
                            <AdvancedMarker key={place.id} position={{ lat: place.latitude, lng: place.longitude }} title={place.name}>
                              <div className="w-7 h-7 rounded-full flex items-center justify-center text-white shadow-lg border-2 border-white bg-primary">
                                <Compass className="w-4 h-4" />
                              </div>
                            </AdvancedMarker>
                          );
                        }
                        return (
                          <AdvancedMarker key={place.id} position={{ lat: place.latitude, lng: place.longitude }} title={place.name}>
                            <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg border-2 border-white" style={{ backgroundColor: cat.hex }}>{i}</div>
                          </AdvancedMarker>
                        );
                      })}
                    </GoogleMap>
                  </APIProvider>
                  {/* Expand/collapse hint on map */}
                  <button
                    className="absolute bottom-2 right-2 bg-background/90 backdrop-blur rounded-lg px-2 py-1 text-[10px] text-muted-foreground flex items-center gap-1 shadow-sm border"
                    onClick={toggleMapExpand}
                    data-testid="tour-map-toggle"
                  >
                    {mapHeightPct >= MAP_SNAP_DEFAULT ? (
                      <><ChevronDown className="w-3 h-3" /> List</>
                    ) : (
                      <><ChevronUp className="w-3 h-3" /> Map</>
                    )}
                  </button>
                </div>
              )}

              {/* Drag handle */}
              {places.length > 0 && GOOGLE_MAPS_API_KEY && (
                <div
                  className="flex-shrink-0 flex items-center justify-center py-1.5 cursor-row-resize touch-none select-none bg-background border-b"
                  onTouchStart={onTouchStartHandle}
                  onTouchMove={onTouchMoveHandle}
                  onTouchEnd={onTouchEndHandle}
                  onMouseDown={onMouseDownHandle}
                  data-testid="tour-drag-handle"
                >
                  <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
                </div>
              )}

              {/* Results section — scrollable */}
              <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
                <div className="max-w-lg mx-auto px-4 py-3">
                  {/* Heading + Edit filters + Select audio guides */}
                  <div className="flex items-center gap-2 mb-2">
                    <h2 className="text-base font-semibold flex-1">Top highlights</h2>
                    <button className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => setStep(2)} data-testid="tour-edit-filters-btn">
                      <SlidersHorizontal className="w-3 h-3" /> Edit filters
                    </button>
                    {places.length > 0 && (
                      <>
                        <span className="text-muted-foreground/30">|</span>
                        <button
                          className={`text-xs font-medium px-2.5 py-1 rounded-lg border transition-all ${
                            selectionMode ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/30"
                          }`}
                          onClick={() => {
                            if (selectionMode) { setSelectionMode(false); setSelectedPlaces(new Set()); }
                            else setSelectionMode(true);
                          }}
                          data-testid="tour-selection-mode-btn"
                        >
                          <Headphones className="w-3 h-3 inline mr-1" />
                          {selectionMode ? `${numSelected} selected` : "Select guides"}
                        </button>
                      </>
                    )}
                  </div>

                  {/* Select all / Deselect all */}
                  {selectionMode && (
                    <div className="flex justify-end mb-1">
                      <button className="text-xs text-primary hover:underline" data-testid="tour-select-all-btn"
                        onClick={() => {
                          if (numSelected === purchasablePlaces.length) setSelectedPlaces(new Set());
                          else setSelectedPlaces(new Set(purchasablePlaces.map(p => p.id)));
                        }}>
                        {numSelected === purchasablePlaces.length ? "Deselect all" : "Select all"}
                      </button>
                    </div>
                  )}

                  {places.length === 0 ? (
                    <div className="py-8 text-center">
                      <MapPin className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
                      <p className="text-sm text-muted-foreground">No places found matching your criteria.</p>
                      <Button variant="outline" className="mt-4 rounded-xl" onClick={() => setStep(2)} data-testid="tour-adjust-filters-btn">
                        <SlidersHorizontal className="w-4 h-4 mr-2" /> Adjust Filters
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        {places.map((place, i) => (
                          <PlaceCard key={place.id} place={place} index={i}
                            selected={selectedPlaces.has(place.id)} selectionMode={selectionMode}
                            onToggle={togglePlace} onView={viewPlace} progress={downloadProgress[place.id]} />
                        ))}
                      </div>

                      {/* Buy all option */}
                      {!allDone && (
                        <Card className="mt-3 border-primary/20 bg-primary/[0.02]" data-testid="tour-buy-all-card">
                          <CardContent className="py-3 px-4">
                            <Button
                              className="w-full h-auto rounded-xl py-3 flex flex-col items-center gap-0.5"
                              onClick={() => openPurchaseConfirm(true)}
                              disabled={purchasing}
                              data-testid="tour-buy-all-btn"
                            >
                              <span className="flex items-center gap-2 text-sm font-semibold text-white">
                                <Package className="w-4 h-4" />
                                Buy pack of {bundleCount} audio guides
                              </span>
                              <span className="text-[11px] text-white/80 font-normal">
                                ({bundleCost} credit{bundleCost !== 1 ? "s" : ""}{hasBundleDiscount ? ` — ${regularCost - bundleCost} free!` : ""})
                              </span>
                            </Button>
                          </CardContent>
                        </Card>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Sticky purchase footer (individual selection) */}
          {selectionMode && numSelected > 0 && !allDone && (
            <div className="fixed bottom-16 left-0 right-0 z-10 px-4 pb-3 pt-2 bg-background/95 backdrop-blur border-t" data-testid="tour-purchase-footer">
              <div className="max-w-lg mx-auto">
                <Button className="w-full h-12 rounded-xl text-base" onClick={() => openPurchaseConfirm(false)} disabled={purchasing} data-testid="tour-purchase-btn">
                  {purchasing ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Processing...</>
                    : <><ShoppingCart className="w-4 h-4 mr-2" /> Buy {numSelected} audio guide{numSelected !== 1 ? "s" : ""} ({selectedCost} credit{selectedCost !== 1 ? "s" : ""})</>}
                </Button>
                {selectedSummary.hasDiscount && (
                  <p className="text-center text-xs text-emerald-600 mt-1">
                    Bundle discount applied: {selectedSummary.savedCredits} guide{selectedSummary.savedCredits !== 1 ? "s" : ""} free
                  </p>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Purchase confirmation dialog */}
      <Dialog open={confirmOpen} onOpenChange={(open) => { if (!open) { setConfirmOpen(false); setBuyingAll(false); } }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirm Purchase</DialogTitle>
            <DialogDescription>
              Buy {confirmCount} audio guide{confirmCount !== 1 ? "s" : ""} for {confirmCredits} credit{confirmCredits !== 1 ? "s" : ""}?
              {confirmCredits < confirmCount && (
                <span className="block mt-1 text-emerald-600 font-medium">
                  Bundle discount applied — saving {confirmCount - confirmCredits} credit{confirmCount - confirmCredits !== 1 ? "s" : ""}!
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => { setConfirmOpen(false); setBuyingAll(false); }}>Cancel</Button>
            <Button className="flex-1" onClick={handlePurchase} data-testid="tour-confirm-purchase-btn">
              <ShoppingCart className="w-4 h-4 mr-2" /> Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Insufficient credits dialog */}
      <Dialog open={insufficientOpen} onOpenChange={setInsufficientOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              <DialogTitle>Not Enough Credits</DialogTitle>
            </div>
            <DialogDescription>
              You need {creditsNeeded} credit{creditsNeeded !== 1 ? "s" : ""} but only have {user?.credits || 0}.
              Top up your credits to continue.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setInsufficientOpen(false)}>Cancel</Button>
            <Button className="flex-1" onClick={() => { setInsufficientOpen(false); navigate("/pricing"); }} data-testid="tour-topup-credits-btn">
              Top up credits
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <BottomNav
        active="/plan-tour"
        onActiveClick={() => {
          // Already on Plan page → reset to step 1
          if (hasPendingDownloads) {
            toast.info("Your audio guides are being generated in the background. Check your Library when ready.", { duration: 3000 });
          }
          sessionStorage.removeItem("cv_tour_last");
          setStep(1);
          setPlaces([]);
          setDestination(null);
          setSearchQuery("");
          setSelectionMode(false);
          setSelectedPlaces(new Set());
          setDownloadProgress({});
          setBuyingAll(false);
        }}
      />
    </div>
  );
};

export default TourPlannerPage;
